import {createSlice, PayloadAction} from '@reduxjs/toolkit';

export enum ENTITY {
    ROADMAP = 'ROADMAP',
    PROJECT = 'PROJECT',
    ROADMAP_GROUP = "ROADMAP_GROUP",
}

export const getLocalStorageItem = (key: string, defaultValue: any) => {
    try {
        const item = localStorage.getItem(key);
        const parsed = item ? JSON.parse(item) : defaultValue;
        // Coerce legacy 'daily' period to 'weekly'
        if ((key === 'projectPeriod' || key === 'roadmapPeriod') && parsed === 'daily') {
            localStorage.setItem(key, JSON.stringify('weekly'));
            return 'weekly';
        }
        return parsed;
    } catch (error) {
        return defaultValue;
    }
};

export const timelineSlice = createSlice({
    name: 'timeline',
    initialState: {
        // General timeline state
        currentEntity: getLocalStorageItem('currentEntity', null),
        currentEntityId: getLocalStorageItem('currentEntityId', null),
        viewType: getLocalStorageItem('viewType', 'timeline'),
        activeTab:  null as 'projects' | 'dependencies' | 'tasks' | 'milestones' | null,
        taskId: "",
        projectId: "",
        dashboardDetails: {},

        // Simulation state (applies to both roadmap and project)
        simulation: getLocalStorageItem('simulation', false),

        // Roadmap timeline state
        projectIdForScroll: getLocalStorageItem('projectIdForScroll', null),
        roadmapName: null,
        roadmapPeriod: getLocalStorageItem('roadmapPeriod', 'monthly'),
        // Persisted auto period selection for roadmap
        roadmapPeriodAuto: getLocalStorageItem('roadmapPeriodAuto', false),

        // Project timeline state
        projectName: null,
        taskIdForScroll: getLocalStorageItem('taskIdForScroll', null),
        projectPeriod: getLocalStorageItem('projectPeriod', 'weekly'),
        // Persisted auto period selection for project
        projectPeriodAuto: getLocalStorageItem('projectPeriodAuto', false),
        // UI state
        suppressDateIndicator: false,
    },
    reducers: {
        setCurrentEntity: (state, action: PayloadAction<{ entity: ENTITY; entityId: string }>) => {
            state.currentEntity = action.payload.entity;
            state.currentEntityId = action.payload.entityId;
            // localStorage.setItem('currentEntity', JSON.stringify(action.payload.entity));
            // localStorage.setItem('currentEntityId', JSON.stringify(action.payload.entityId));
        },
        setActiveTab: (state, action: PayloadAction<'projects' | 'dependencies' | 'tasks' | 'milestones'>) => {
            state.activeTab = action.payload;
        },
        setDashboardDetails: (state, action: PayloadAction<{
            date: string,
            progress: number,
            status: string,
            childEntityType: string,
            childEntityTypes?: Record<string, number> | null,
            entityType: string,
            stats: Object,
            skills: Object[],
            members: Object[],
            sim_data?: any
        }>) => {
            state.dashboardDetails = action.payload;
        },
        resetDashboardDetails: (state) => {
            state.dashboardDetails = {}
        },

        // Simulation reducers
        setSimulation: (state, action: PayloadAction<boolean>) => {
            state.simulation = action.payload;
            // localStorage.setItem('simulation', JSON.stringify(action.payload));
        },

        // Roadmap reducers
        setProjectIdForScroll: (state, action: PayloadAction<string>) => {
            state.projectIdForScroll = action.payload;
            if(action.payload) {
                state.projectId = action.payload;
            }
            localStorage.setItem('projectIdForScroll', JSON.stringify(action.payload));
        },
        resetProjectIdForScroll: (state) => {
            state.projectIdForScroll = null;
            localStorage.removeItem('projectIdForScroll');
        },
        resetSelectedProjectId: (state) => {
            state.projectId = "";
        },
        setRoadmapName: (state, action: PayloadAction<string>) => {
            state.roadmapName = action.payload;
            localStorage.setItem('roadmapName', JSON.stringify(action.payload));
        },
        resetRoadmapName: (state) => {
            state.roadmapName = null;
            localStorage.removeItem('roadmapName');
        },
        setRoadmapPeriod: (state, action: PayloadAction<string>) => {
            state.roadmapPeriod = action.payload;
            localStorage.setItem('roadmapPeriod', JSON.stringify(action.payload));
        },
        setRoadmapPeriodAuto: (state, action: PayloadAction<boolean>) => {
            state.roadmapPeriodAuto = action.payload;
            localStorage.setItem('roadmapPeriodAuto', JSON.stringify(action.payload));
        },
        setViewType: (state, action: PayloadAction<"timeline" | "list">) => {
            state.viewType = action.payload;
            localStorage.setItem('viewType', JSON.stringify(action.payload));
        },
        // Project reducers
        setProjectName: (state, action: PayloadAction<string>) => {
            state.projectName = action.payload;
            localStorage.setItem('projectName', JSON.stringify(action.payload));
        },
        resetProjectName: (state) => {
            state.projectName = null;
            localStorage.removeItem('projectName');
        },
        setTaskIdForScroll: (state, action: PayloadAction<string>) => {
            state.taskIdForScroll = action.payload;
            if(action.payload) {
                state.taskId = action.payload;
            }
            localStorage.setItem('taskIdForScroll', JSON.stringify(action.payload));
        },
        resetSelectedTaskId: (state) => {
            state.taskId = "";
        },
        resetTaskIdForScroll: (state) => {
            state.taskIdForScroll = null;
            localStorage.removeItem('taskIdForScroll');
        },
        setProjectPeriod: (state, action: PayloadAction<string>) => {
            // Coerce 'daily' inputs to 'weekly' to be safe
            const next = action.payload === 'daily' ? 'weekly' : action.payload;
            state.projectPeriod = next;
            localStorage.setItem('projectPeriod', JSON.stringify(next));
        },
        setProjectPeriodAuto: (state, action: PayloadAction<boolean>) => {
            state.projectPeriodAuto = action.payload;
            localStorage.setItem('projectPeriodAuto', JSON.stringify(action.payload));
        },
        setSuppressDateIndicator: (state, action: PayloadAction<boolean>) => {
            state.suppressDateIndicator = action.payload;
        },

        // Reset all timeline state
        resetTimelineState: (state) => {
            // Reset general state
            state.currentEntity = null;
            state.currentEntityId = null;
            state.activeTab = null;
            state.dashboardDetails = {};

            // Reset roadmap state
            state.projectIdForScroll = null;
            state.roadmapName = null;

            state.simulation = false;

            // Reset project state
            state.projectName = null;
            state.taskIdForScroll = null;

            // Clear localStorage
            localStorage.removeItem('projectName');
            localStorage.removeItem('simulation');
            localStorage.removeItem('roadmapName');
            localStorage.removeItem('currentEntity');
            localStorage.removeItem('currentEntityId');
            localStorage.removeItem('projectIdForScroll');
            localStorage.removeItem('taskIdForScroll');
        },
        softResetTimelineState: (state) => {
            // Reset general state
            state.currentEntity = null;
            state.currentEntityId = null;
            state.activeTab = null;
            state.dashboardDetails = {};

            // Reset roadmap state
            state.roadmapName = null;

            // Reset project state
            state.projectName = null;

            // Clear localStorage
            localStorage.removeItem('projectName');
            localStorage.removeItem('simulation');
            localStorage.removeItem('roadmapName');
            localStorage.removeItem('currentEntity');
            localStorage.removeItem('currentEntityId');
        },
        resetTimelineStateFromStrategyMap: (state) => {
            // Reset general state
            state.currentEntity = null;
            state.currentEntityId = null;
            state.dashboardDetails = {};

            // Reset roadmap state
            state.projectIdForScroll = null;
            state.roadmapName = null;

            // Reset project state
            state.projectName = null;
            state.taskIdForScroll = null;

            // Clear localStorage
            localStorage.removeItem('projectName');
            localStorage.removeItem('simulation');
            localStorage.removeItem('roadmapName');
            localStorage.removeItem('currentEntity');
            localStorage.removeItem('currentEntityId');
            localStorage.removeItem('projectIdForScroll');
            localStorage.removeItem('taskIdForScroll');
        },
    }
});

export const {
    // General actions
    setCurrentEntity,
    setActiveTab,
    setDashboardDetails,
    resetDashboardDetails,
    setViewType,
    
    // Simulation actions
    setSimulation,
    
    // Roadmap actions
    setProjectIdForScroll,
    resetProjectIdForScroll,
    resetSelectedProjectId,
    setRoadmapName,
    resetRoadmapName,
    setRoadmapPeriod,
    setRoadmapPeriodAuto,

    // Project actions
    setProjectName,
    resetProjectName,
    setTaskIdForScroll,
    resetTaskIdForScroll,
    resetSelectedTaskId,
    setProjectPeriod,
    setProjectPeriodAuto,
    setSuppressDateIndicator,
    resetTimelineStateFromStrategyMap,
    softResetTimelineState,
    // Reset action
    resetTimelineState
} = timelineSlice.actions;

export default timelineSlice.reducer; 